import { InfoCircle } from '@untitledui/icons';

import { cx } from '@/utils/cx';

interface RelatedThreadsTruncatedNoticeProps {
  /**
   * Compact rendering for the sidebar timeline section (smaller text/padding/icon).
   * Defaults to the full-size treatment used by the main record timeline.
   */
  compact?: boolean;
}

/**
 * Passive notice shown at the oldest end of a contact's timeline when the
 * backend capped the related email-thread fan-out to the most-recently-active
 * threads (see RELATED_THREAD_CAP). Honest signal that older email activity
 * exists but isn't aggregated onto this contact's timeline.
 *
 * Shared by the full timeline (TimelineEventList footer) and the sidebar
 * preview (TimelineSection) so the copy and styling stay in one place.
 */
export function RelatedThreadsTruncatedNotice({
  compact = false,
}: RelatedThreadsTruncatedNoticeProps) {
  return (
    <div className={cx('flex', compact ? 'pt-2' : 'justify-center py-4')}>
      {/* Self-contained callout: a faint surface fill groups the icon with the
          left-aligned copy so it reads as an intentional system note rather
          than stray centered text. */}
      <div
        className={cx(
          'flex items-start gap-2 rounded-lg bg-secondary text-left',
          compact ? 'w-full px-3 py-2' : 'max-w-md px-3.5 py-2.5'
        )}
      >
        <InfoCircle
          className={cx(
            'mt-px shrink-0 text-quaternary',
            compact ? 'size-3.5' : 'size-4'
          )}
        />
        <span
          className={cx(
            'text-tertiary',
            compact ? 'text-xs leading-snug' : 'text-sm leading-normal'
          )}
        >
          Only the most recently active email threads are shown. Older
          conversations for this contact are not included.
        </span>
      </div>
    </div>
  );
}
